import { Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

//STYLE
import { FooterAcoes, ConteudoGrid, ColunaEsquerda } from './styled';

//APP
import { TipoColeta } from '../../../../meta';
import { useAnalises } from './hooks/useAnalises';
import { FormOsProvider } from './context/FormOsContext';
import { useFormularioOs } from './hooks/useFormularioOs';
import { usePermissoesOs } from '../../../../meta/usePermissoesOs'
import { motivoSemClienteGlobal } from '../../meta/acessoCliente'
import { localConfig } from '../../../../../../../../Configs/localConfig';
import { useGetOsColetadaEditar } from '../../../../../../../../services/OsParaColeta/GET';
import { montarAnalisesPayload, montarArquivoKml } from './meta';
import { pontosForaDoContorno } from './components/analises/meta';

//COMPONENTS
import Carregando from '../../../../../../../../Components/CustomUi/loading';
import ButtonCustom from '../../../../../../../../Components/ButtonCustom';
import { Analises } from './components/analises';
import { AlertaEdicao } from './components/alertaEdicao';
import { DrawerCustom } from '../../../../../../../../Components/CustomUi/Drawer';
import { DetalhesCliente } from './components/detalhesCliente';
import { CamposFormulario } from './components/camposFormulario';
import { ConfirmarTrocaLab } from './components/confirmarTrocaLab';

interface Props {
    abrirGaveta: boolean
    fecharGaveta: () => void
    /** Atualiza a lista de OS após salvar. */
    refetchLista: () => void
    /** cod da OS a editar (null quando criando/copiando). */
    update?: number | null
    /** cod da OS a copiar (null quando criando/editando). */
    copiar?: number | null
    /** Tipo de coleta do submódulo dono da gaveta (pré-seleciona o "Tipo da análise"). */
    tipo_coleta?: TipoColeta
    /** Filtro da listagem: pré-preenche a propriedade ao criar. */
    filtro_propriedade?: { cod: number | null; nome: string }
}

/**
 * Gaveta de cadastro/edição/cópia de OS de coleta.
 * O estado do formulário vive nos hooks useFormularioOs/useAnalises e é exposto
 * via context, evitando o prop drilling do módulo antigo.
 */
export const FormularioOsColeta: React.FC<Props> = ({ abrirGaveta, fecharGaveta, refetchLista, update, copiar, tipo_coleta = 'Solo', filtro_propriedade }) => {

    const cod_os = update ?? copiar ?? null
    const modo = update ? 'editar' : copiar ? 'copiar' : 'criar'

    const form = useFormularioOs({ onClose: fecharGaveta, cod_os, modo, tipo_coleta, filtro_propriedade })
    const analises = useAnalises({
        cod_laboratorio: form.campos.laboratorio_id,
        data_vigencia: form.campos.data_da_os,
    })

    //#region API
    // Busca a OS ao editar/copiar (react-query; não bloqueia a abertura da gaveta).
    const { data_osColeta_editar, carregando_osColeta_editar, refetch_data_osColeta_editar } =
        useGetOsColetadaEditar(cod_os ?? 0)

    const permissoes = usePermissoesOs()

    const ctx = { ...form, analises, permissoes }

    // Aviso de que editar a OS compromete a sincronização do app no campo.
    const [alertaEdicao, setAlertaEdicao] = useState(false)

    // Adia a montagem do conteúdo pesado (campos + análises com muitos pontos) para
    // depois da animação de abertura da gaveta — evita o "engasgo" ao abrir.
    const [conteudoPronto, setConteudoPronto] = useState(false)

    const { resetForm } = form
    const { resetAnalises } = analises

    useEffect(() => {
        if (!abrirGaveta) {
            setConteudoPronto(false)
            setAlertaEdicao(false)

            // A gaveta fica SEMPRE MONTADA (para abrir mesmo com a lista vazia),
            // então o estado não morre junto com ela: sem esta limpeza, abrir o
            // cadastro depois de uma edição mostrava os dados da OS anterior.
            // Fica aqui, e não no `fechar`, porque o salvar chama o `onClose` do
            // módulo direto — não passava pelo reset.
            resetForm()
            resetAnalises()
            return
        }
        const timer = setTimeout(() => setConteudoPronto(true), 150)
        return () => clearTimeout(timer)
    }, [abrirGaveta, resetForm, resetAnalises])

    // Ao abrir em modo editar/copiar, busca a OS e pré-preenche form + análises.
    const { hidratar } = form
    const { hidratarAnalises } = analises
    useEffect(() => {
        if (abrirGaveta && cod_os) {
            refetch_data_osColeta_editar()
        }
    }, [abrirGaveta, cod_os, refetch_data_osColeta_editar])

    useEffect(() => {
        const os = data_osColeta_editar?.itens?.[0]
        // Quando o backend devolve o `cod`, ele precisa bater com a OS aberta: o
        // cache do react-query ainda guarda a anterior, e uma resposta atrasada
        // hidrataria o formulário com os dados dela. A checagem é condicional
        // porque `cod` é opcional no retorno — exigi-lo bloquearia a edição.
        const osCorresponde = os?.cod === undefined || os.cod === cod_os
        if (abrirGaveta && cod_os && os && osCorresponde) {
            hidratar(os)
            hidratarAnalises(os)

            // Espelha a produção: o aviso de sincronização aparece ao terminar de
            // carregar a OS para EDIÇÃO — não ao criar nem ao clonar.
            if (modo === 'editar') {
                setAlertaEdicao(true)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abrirGaveta, cod_os, data_osColeta_editar, modo])

    // Sincroniza área e contorno do talhão selecionado (metade esquerda) para a metade "Análises".
    // Vêm do talhão, não da malha importada — habilitam/alimentam "Editar contorno" e "Nova análise".
    const { area_talhao, contorno_talhao } = form
    const { setTalhaoArea, setTalhaoKml, contornoEditado } = analises

    useEffect(() => {
        if (area_talhao) setTalhaoArea(area_talhao)
    }, [area_talhao, setTalhaoArea])

    useEffect(() => {
        // Depois que o usuário editou o contorno no mapa, o contorno do talhão não
        // pode mais sobrescrevê-lo — senão a edição é descartada antes de salvar.
        if (contornoEditado) return
        if (contorno_talhao.length > 0) setTalhaoKml(contorno_talhao)
    }, [contorno_talhao, setTalhaoKml, contornoEditado])

    const fechar = () => {
        form.resetForm()
        analises.resetAnalises()
        fecharGaveta()
    }

    /**
     * Desfaz a troca de laboratório: o hook devolve o cod anterior e aqui o Select
     * é restaurado (label + id), já que o formulário é quem detém esse campo.
     */
    const desfazerTrocaLaboratorio = () => {
        const codAnterior = analises.cancelarTrocaLaboratorio()
        if (codAnterior === null) return

        const lab = (form.data_laboratorios ?? []).find((l: any) => l?.cod === codAnterior)
        form.setCampo('laboratorio_id', codAnterior)
        form.setCampo('laboratorio', lab?.nome ?? '')
    }

    const salvar = () => {
        const dadosAnalise = montarAnalisesPayload(analises.analises, analises.malhaShp)

        // A importação de shapefile aceita pontos fora do talhão (criar e editar
        // barram no próprio gesto); é aqui que a OS é bloqueada.
        const pontos_fora = pontosForaDoContorno(analises.malha, analises.talhaoKml)

        // O contorno só entra no payload quando o usuário o editou no mapa —
        // reenviá-lo sempre reescreveria no backend o mesmo contorno já gravado.
        const arquivo_kml = analises.contornoEditado
            ? montarArquivoKml(analises.talhaoKml, form.campos.talhao)
            : null

        form.salvar(refetchLista, { ...dadosAnalise, arquivo_kml, pontos_fora })
    }

    const possuiAnalises = analises.analises.length > 0

    /**
     * Sem cliente no filtro global a gaveta abre em SOMENTE LEITURA: o formulário
     * salva com o `codCliente` da querystring, que estaria vazio, e a OS iria
     * para o cliente errado. Ver e imprimir seguem liberados.
     */
    const motivo_sem_cliente = motivoSemClienteGlobal(localConfig()?.codCliente)

    // O botão fica ativo mesmo com pendências: a validação do salvar aponta o que
    // falta. Desabilitar só por "sem análises" escondia os demais campos faltantes.
    const desabilitarSalvar = form.carregando_salvar_os || !!motivo_sem_cliente

    const titulo = modo === 'editar' ? 'Editar O.S' : modo === 'copiar' ? 'Clonar O.S' : 'Cadastrar O.S'
    // Só mostra o conteúdo quando montado (defer) e, no editar/copiar, com a OS já carregada.
    const mostrarConteudo = conteudoPronto && !(cod_os && carregando_osColeta_editar)

    return (
        <FormOsProvider value={ctx}>
            <DrawerCustom
                destroyOnClose
                width={1080}
                open={abrirGaveta}
                onClose={fechar}
                paddingBody={16}
                title={titulo}
                confirmOnClose={form.isDirty || analises.analisesDirty}
                childrenFooter={
                    <FooterAcoes>
                        <Tooltip title={motivo_sem_cliente ?? (!possuiAnalises ? 'Adicione ao menos uma análise para salvar' : undefined)}>
                            <span>
                                <ButtonCustom
                                    icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                                    type='primary'
                                    onClick={salvar}
                                    loading={form.carregando_salvar_os}
                                    disabled={desabilitarSalvar}
                                >
                                    {modo === 'editar' ? 'Atualizar' : 'Salvar'}
                                </ButtonCustom>
                            </span>
                        </Tooltip>
                    </FooterAcoes>
                }
            >
                {mostrarConteudo ? (
                    <ConteudoGrid>
                        {/* Metade esquerda: detalhes do cliente + campos do formulário */}
                        <ColunaEsquerda>
                            <DetalhesCliente cliente={form.data_cliente} />
                            <CamposFormulario />
                        </ColunaEsquerda>

                        {/* Metade direita: análises */}
                        <Analises />
                    </ConteudoGrid>
                ) : (
                    <Carregando animation justifyContent='center' height='400px' legenda='Carregando formulário...' />
                )}

                {/* Só depois do loading: o alerta sobre a gaveta ainda carregando
                    esconde o conteúdo que o usuário está esperando ver. */}
                <AlertaEdicao
                    abrir={alertaEdicao && mostrarConteudo}
                    fechar={() => setAlertaEdicao(false)}
                />

                <ConfirmarTrocaLab
                    abrir={analises.abrirConfirmarTrocaLab}
                    analises_nao_encontradas={analises.analisesNaoEncontradas}
                    aoConfirmar={analises.confirmarTrocaLaboratorio}
                    aoCancelar={desfazerTrocaLaboratorio}
                />
            </DrawerCustom>
        </FormOsProvider>
    )
}
